/**
 * Offline orders: IndexedDB da saqlash, ulanish tiklanganda /sync/orders ga yuborish.
 * PWA & Offline POS (TZ items 2, 5, 15).
 */
import { buildPosOrderRequestBody } from './ordersApi';

const DB_NAME = 'neduz-offline';
const DB_VERSION = 1;
const STORE_ORDERS = 'pending_orders';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_ORDERS)) {
        db.createObjectStore(STORE_ORDERS, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
}

export async function saveOrderOffline(payload) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ORDERS, 'readwrite');
    const store = tx.objectStore(STORE_ORDERS);
    const record = {
      ...payload,
      createdAt: Date.now(),
      synced: false,
      status: 'pending',
    };
    const req = store.add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function getPendingOrders() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ORDERS, 'readonly');
    const req = tx.objectStore(STORE_ORDERS).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function markOrderSynced(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ORDERS, 'readwrite');
    const store = tx.objectStore(STORE_ORDERS);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (row) {
        row.synced = true;
        store.put(row);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
    tx.oncomplete = () => db.close();
  });
}

export async function deleteSyncedOrder(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ORDERS, 'readwrite');
    tx.objectStore(STORE_ORDERS).delete(id);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Ulanish tiklanganda pending buyurtmalarni /sync/orders ga yuborish.
 * api import qilmaslik uchun fetch ishlatamiz (base URL env dan).
 */
function getBaseUrl() {
  try {
    const url = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';
    return String(url).replace(/\/$/, '');
  } catch {
    return '';
  }
}

function getAuthToken() {
  try {
    return window.localStorage?.getItem?.('auth_token') || window.localStorage?.getItem?.('access_token') || '';
  } catch {
    return '';
  }
}

export async function syncPendingOrders() {
  if (!navigator.onLine) return { synced: 0, failed: 0 };
  const pending = await getPendingOrders();
  const unsynced = pending.filter((o) => !o.synced);
  if (unsynced.length === 0) return { synced: 0, failed: 0 };
  const base = getBaseUrl();
  const syncUrl = base ? `${base}/api/sync/orders` : '/api/sync/orders';
  const token = getAuthToken();
  let synced = 0;
  let failed = 0;
  for (const order of unsynced) {
    try {
      const { id, createdAt, synced, status, ...rest } = order;
      const body = buildPosOrderRequestBody(rest);
      const res = await fetch(syncUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        await markOrderSynced(order.id);
        await deleteSyncedOrder(order.id);
        synced++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }
  return { synced, failed };
}

/** Online bo‘lganda va visibility change da sync ni ishga tushirish */
export function initOfflineSync() {
  const run = () => syncPendingOrders();
  if (typeof window === 'undefined') return;
  window.addEventListener('online', run);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') run();
  });
}
